import React from "react";
import { Column } from "@/components/Datatable/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import EventSessionActionMenu from "@/modules/eventRegistration/features/EventSessionManagement/components/EventSessionActionMenu/EventSessionActionMenu";
import { SessionSummary } from "@/modules/eventRegistration/features/EventSessionManagement/api/fetchEventSessions/types";
import { formatSessionDateTime } from "@/modules/eventRegistration/shared/utils/dateFormatting";

interface CreateEventSessionColumnsProps {
  onViewSession: (sessionUuid: string) => void;
  onDeleteSession: (uuid: string) => void;
  onCancelSession?: (uuid: string) => void;
  onEditSession?: (uuid: string) => void;
  onDuplicateSession?: (uuid: string) => void;
}

export function createEventSessionColumns({
  onViewSession,
  onDeleteSession,
  onCancelSession,
  onEditSession,
  onDuplicateSession,
}: CreateEventSessionColumnsProps): Column<SessionSummary>[] {
  return [
    {
      header: "Session Name",
      accessorKey: "name",
      enableSorting: true,
      cell: ({ row }) => row.original.name || "--",
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { sessionDays, startDate, timezoneIdentifier, timezoneShortName } =
          row.original;
        // Prefer the first scheduled day's time over the session's bare
        // startDate, since the daily schedule is what attendees actually see.
        const firstDayStart = sessionDays?.[0]?.startDate ?? startDate;
        return formatSessionDateTime(
          firstDayStart,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "End Date",
      accessorKey: "endDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { sessionDays, endDate, timezoneIdentifier, timezoneShortName } =
          row.original;
        const lastDayEnd =
          sessionDays && sessionDays.length > 0
            ? sessionDays[sessionDays.length - 1].endDate
            : endDate;
        return formatSessionDateTime(
          lastDayEnd,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "Registered",
      id: "registered",
      cell: ({ row }) => {
        const { registeredCount, maxEnrollments } = row.original;
        return (
          <span>
            {registeredCount}
            {typeof maxEnrollments === "number" && maxEnrollments > 0 && (
              <span className="text-gray-400 dark:text-gray-500">
                {" "}
                / {maxEnrollments}
              </span>
            )}
          </span>
        );
      },
    },
    {
      header: "Waitlisted",
      id: "waitlisted",
      cell: ({ row }) => {
        const { waitlistedCount } = row.original;
        if (waitlistedCount === 0) {
          return (
            <span className="text-gray-400 dark:text-gray-500">0</span>
          );
        }
        return (
          <span className="text-amber-600 dark:text-amber-400">
            {waitlistedCount}
          </span>
        );
      },
    },
    {
      header: "Published",
      accessorKey: "isPublished",
      enableSorting: true,
      cell: ({ row }) => {
        const { isPublished } = row.original;
        return isPublished ? (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
            Published
          </Badge>
        ) : (
          <Badge
            className="bg-gray-50 text-gray-500 border-gray-200 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-700"
            variant="outline"
          >
            Draft
          </Badge>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "isFinalized",
      enableSorting: true,
      cell: ({ row }) => {
        const { isFinalized, isCancelled } = row.original;

        if (isCancelled) {
          return (
            <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
              <XCircle className="h-3 w-3 mr-1" />
              Cancelled
            </Badge>
          );
        }

        if (isFinalized) {
          return (
            <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Finalized
            </Badge>
          );
        }

        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
            <Clock className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      },
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => {
        const session = row.original;
        return (
          <div
            className="flex items-center justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              onClick={() => onViewSession(session.uuid)}
              size="sm"
              variant="outline"
            >
              View Session
            </Button>
            <EventSessionActionMenu
              isCancelled={session.isCancelled}
              onCancelSession={onCancelSession}
              onDeleteSession={onDeleteSession}
              onDuplicateSession={onDuplicateSession}
              onEditSession={onEditSession}
              sessionUuid={session.uuid}
            />
          </div>
        );
      },
    },
  ];
}
